import React, { useEffect } from 'react'
import { Input, Form, message, Typography } from 'antd'
import {
    BarChartOutlined,
    CloudServerOutlined,
    LockOutlined,
    ThunderboltOutlined,
    UserOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import AuthForm from '../components/common/AuthForm/AuthForm'
import { useAuth } from '../hooks/useAuth'
import { useAuthForm } from '../hooks/useAuthForm'
import { LoginFormValues } from '../types/form'
import {
    AUTH_REDIRECT_REASON_ACCOUNT_CHANGED,
    AUTH_REDIRECT_REASON_PERMISSION_CHANGED,
    AUTH_REDIRECT_REASON_PASSWORD_CHANGED,
    AUTH_REDIRECT_REASON_STORAGE_KEY,
    POST_LOGIN_REDIRECT_STORAGE_KEY,
} from '../utils/axiosConfig'

const { Paragraph, Text, Title } = Typography

const formulaGhosts = [
    'f(x)=sum(w_i*x_i)+b',
    'P(t)=lambda e^(-lambda t)',
    'E=-sum(p_i log p_i)',
    'vector<T> stream',
    'S(t+1)=A*S(t)+u_t',
]

const Login = (): React.ReactElement => {
    const { t } = useTranslation(['auth', 'errors'])
    const { login, refreshUser } = useAuth()
    const { loading, error, handleSubmit, state } = useAuthForm('/')
    const heroTitle = t('login.heroTitle')
    const heroTitleSegments = [
        {
            key: 'lead',
            text: t('login.heroTitleLead'),
        },
        {
            key: 'main',
            text: t('login.heroTitleMain'),
        },
    ]

    const metricCards = [
        {
            key: 'uptime',
            value: t('login.metrics.uptime.value'),
            label: t('login.metrics.uptime.label'),
        },
        {
            key: 'ai',
            value: t('login.metrics.ai.value'),
            label: t('login.metrics.ai.label'),
        },
        {
            key: 'iot',
            value: t('login.metrics.iot.value'),
            label: t('login.metrics.iot.label'),
        },
    ]

    const operationCards = [
        {
            key: 'control',
            icon: <ThunderboltOutlined />,
            title: t('login.operations.control.title'),
            description: t('login.operations.control.description'),
        },
        {
            key: 'collaboration',
            icon: <CloudServerOutlined />,
            title: t('login.operations.collaboration.title'),
            description: t('login.operations.collaboration.description'),
        },
    ]

    useEffect(() => {
        const redirectReason = sessionStorage.getItem(
            AUTH_REDIRECT_REASON_STORAGE_KEY
        )

        const redirectReasonMessages: Record<string, string> = {
            [AUTH_REDIRECT_REASON_PERMISSION_CHANGED]: t(
                'errors:auth.permissionChanged',
                {
                    defaultValue: 'Permissions changed. Please log in again.',
                }
            ),
            [AUTH_REDIRECT_REASON_PASSWORD_CHANGED]: t(
                'errors:auth.passwordChanged',
                {
                    defaultValue: 'Password changed. Please log in again.',
                }
            ),
            [AUTH_REDIRECT_REASON_ACCOUNT_CHANGED]: t(
                'errors:auth.accountChanged',
                {
                    defaultValue:
                        'Account information changed. Please log in again.',
                }
            ),
        }

        const redirectReasonMessage = redirectReason
            ? redirectReasonMessages[redirectReason]
            : undefined
        if (redirectReasonMessage) {
            message.error(redirectReasonMessage)
        }

        if (redirectReason) {
            sessionStorage.removeItem(AUTH_REDIRECT_REASON_STORAGE_KEY)
        }
    }, [t])

    const onFinish = async (values: LoginFormValues): Promise<void> => {
        await handleSubmit(
            async () => {
                const success = await login(values.account, values.password)
                if (!success) {
                    throw new Error(t('login.loginFailed'))
                }

                const currentUser = await refreshUser()
                if (!currentUser) {
                    throw new Error(t('login.loginFailed'))
                }

                sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY)

                return { to: '/' }
            },
            values,
            t('login.loginSuccess'),
            (redirectTarget) => redirectTarget
        )
    }

    return (
        <main className="login-page-shell">
            <section
                className="login-visual-panel"
                aria-label={t('login.visualAria')}
            >
                <div className="login-tech-background" aria-hidden="true">
                    <svg
                        className="login-tech-network"
                        data-placement="top-right"
                        data-size="highlight-frame"
                        viewBox="0 0 900 640"
                        preserveAspectRatio="xMidYMid slice"
                        focusable="false"
                    >
                        <defs>
                            <linearGradient
                                id="login-wire-gradient"
                                x1="0%"
                                y1="0%"
                                x2="100%"
                                y2="100%"
                            >
                                <stop
                                    offset="0%"
                                    stopColor="rgba(111, 246, 248, 0.56)"
                                />
                                <stop
                                    offset="52%"
                                    stopColor="rgba(138, 201, 214, 0.2)"
                                />
                                <stop
                                    offset="100%"
                                    stopColor="rgba(68, 181, 206, 0.42)"
                                />
                            </linearGradient>
                            <radialGradient id="login-node-glow">
                                <stop
                                    offset="0%"
                                    stopColor="rgba(168, 255, 255, 0.9)"
                                />
                                <stop
                                    offset="100%"
                                    stopColor="rgba(64, 223, 232, 0)"
                                />
                            </radialGradient>
                        </defs>

                        <g className="login-tech-facets">
                            <polygon points="34,174 86,42 202,118 158,256" />
                            <polygon points="86,42 248,34 202,118" />
                            <polygon points="202,118 312,218 158,256" />
                            <polygon points="332,22 544,34 498,184 410,240" />
                            <polygon points="410,240 498,184 632,326 512,420" />
                            <polygon points="650,384 794,298 858,492 718,568" />
                            <polygon points="18,424 112,342 214,444 144,570" />
                            <polygon points="236,514 384,486 512,420 438,604" />
                            <polygon points="584,116 700,196 824,120 768,42" />
                            <polygon points="690,244 794,298 734,382 632,326" />
                        </g>

                        <g className="login-tech-mesh login-tech-mesh-primary">
                            <polyline points="34,174 86,42 202,118 312,218 410,240 498,184 544,34" />
                            <polyline points="86,42 248,34 410,240 512,420 632,326 794,298 858,492" />
                            <polyline points="158,256 312,218 498,184 632,326 718,568" />
                            <polyline points="332,22 410,240 312,218 202,118" />
                            <polyline points="512,420 650,384 794,298 858,492 718,568 632,326" />
                            <polyline points="18,424 112,342 214,444 236,514 438,604 718,568" />
                            <polyline points="584,116 700,196 824,120 768,42 544,34" />
                            <polyline points="632,326 690,244 794,298 734,382 650,384" />
                            <polyline points="144,570 214,444 384,486 512,420" />
                        </g>

                        <g className="login-tech-mesh login-tech-mesh-secondary">
                            <line x1="12" y1="346" x2="214" y2="244" />
                            <line x1="236" y1="514" x2="410" y2="240" />
                            <line x1="498" y1="184" x2="824" y2="120" />
                            <line x1="544" y1="34" x2="700" y2="196" />
                            <line x1="650" y1="384" x2="884" y2="602" />
                            <line x1="76" y1="608" x2="312" y2="218" />
                            <line x1="18" y1="424" x2="144" y2="570" />
                            <line x1="112" y1="342" x2="384" y2="486" />
                            <line x1="214" y1="444" x2="410" y2="240" />
                            <line x1="438" y1="604" x2="632" y2="326" />
                            <line x1="584" y1="116" x2="690" y2="244" />
                            <line x1="700" y1="196" x2="734" y2="382" />
                            <line x1="768" y1="42" x2="858" y2="492" />
                            <line x1="248" y1="34" x2="112" y2="342" />
                        </g>

                        <g className="login-tech-nodes">
                            <circle cx="34" cy="174" r="4" />
                            <circle cx="86" cy="42" r="3" />
                            <circle cx="202" cy="118" r="4" />
                            <circle cx="312" cy="218" r="3.5" />
                            <circle cx="410" cy="240" r="4" />
                            <circle cx="498" cy="184" r="3" />
                            <circle cx="544" cy="34" r="3.5" />
                            <circle cx="632" cy="326" r="4" />
                            <circle cx="794" cy="298" r="3.5" />
                            <circle cx="858" cy="492" r="4" />
                            <circle cx="718" cy="568" r="3" />
                            <circle cx="18" cy="424" r="3.5" />
                            <circle cx="112" cy="342" r="3" />
                            <circle cx="214" cy="444" r="4" />
                            <circle cx="144" cy="570" r="3" />
                            <circle cx="236" cy="514" r="3.5" />
                            <circle cx="384" cy="486" r="3" />
                            <circle cx="438" cy="604" r="3.5" />
                            <circle cx="584" cy="116" r="3" />
                            <circle cx="700" cy="196" r="3.5" />
                            <circle cx="824" cy="120" r="3" />
                            <circle cx="768" cy="42" r="3.5" />
                            <circle cx="734" cy="382" r="3" />
                        </g>

                        <g className="login-tech-squares">
                            <rect x="110" y="302" width="8" height="8" />
                            <rect x="584" y="116" width="7" height="7" />
                            <rect x="726" y="206" width="8" height="8" />
                            <rect x="812" y="48" width="6" height="6" />
                            <rect x="446" y="548" width="7" height="7" />
                            <rect x="204" y="432" width="6" height="6" />
                            <rect x="372" y="474" width="7" height="7" />
                            <rect x="642" y="370" width="6" height="6" />
                            <rect x="686" y="232" width="7" height="7" />
                        </g>
                    </svg>

                    <div className="login-formula-layer">
                        {formulaGhosts.map((formula, index) => (
                            <span
                                className={`login-formula-ghost login-formula-ghost-${index + 1}`}
                                key={formula}
                            >
                                {formula}
                            </span>
                        ))}
                    </div>
                    <span className="login-hud-chip login-hud-chip-a" />
                    <span className="login-hud-chip login-hud-chip-b" />
                </div>

                <div className="login-visual-content">
                    <div className="login-brand-row">
                        <span className="login-brand-mark" aria-hidden="true">
                            <BarChartOutlined />
                        </span>
                        <Text className="login-brand-name">
                            {t('login.brandName')}
                        </Text>
                    </div>

                    <div className="login-hero-copy">
                        <Text className="login-hero-kicker">
                            {t('login.kicker')}
                        </Text>
                        <Title
                            aria-label={heroTitle}
                            className="login-hero-title"
                            level={1}
                        >
                            <span
                                className="login-hero-title-segment login-hero-title-segment--lead"
                                data-text={heroTitleSegments[0].text}
                            >
                                {heroTitleSegments[0].text}
                            </span>
                            <span
                                className="login-hero-title-module"
                                aria-hidden="true"
                            />
                            <span
                                className="login-hero-title-segment login-hero-title-segment--main"
                                data-text={heroTitleSegments[1].text}
                            >
                                {heroTitleSegments[1].text}
                            </span>
                        </Title>
                        <Paragraph className="login-hero-subtitle">
                            {t('login.heroSubtitle')}
                        </Paragraph>
                    </div>

                    <div className="login-metric-grid" aria-hidden="true">
                        {metricCards.map((metric) => (
                            <article
                                className="login-metric-card"
                                key={metric.key}
                            >
                                <Text className="login-metric-value">
                                    {metric.value}
                                </Text>
                                <Text className="login-metric-label">
                                    {metric.label}
                                </Text>
                                <span className="login-metric-pulse" />
                            </article>
                        ))}
                    </div>

                    <div className="login-operation-list">
                        {operationCards.map((operation) => (
                            <article
                                className="login-operation-card"
                                key={operation.key}
                            >
                                <span
                                    className="login-operation-icon"
                                    aria-hidden="true"
                                >
                                    {operation.icon}
                                </span>
                                <div>
                                    <Text strong>{operation.title}</Text>
                                    <Paragraph>
                                        {operation.description}
                                    </Paragraph>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="login-form-panel" aria-label={t('login.formAria')}>
                <AuthForm<LoginFormValues>
                    title={t('login.title')}
                    subtitle={t('login.subtitle')}
                    eyebrow={t('login.formEyebrow')}
                    formName="login"
                    onFinish={onFinish}
                    loading={loading}
                    error={error}
                    successMessage={state?.message}
                    submitText={t('login.loginButton')}
                    containerClassName="login-auth-container"
                    cardClassName="login-auth-card login-auth-card--cpu-slot"
                >
                    <Form.Item
                        name="account"
                        rules={[
                            {
                                required: true,
                                message: t('validation.accountRequired'),
                            },
                        ]}
                    >
                        <Input
                            className="login-autofill-field"
                            prefix={<UserOutlined />}
                            placeholder={t('login.accountPlaceholder')}
                            size="large"
                            autoComplete="username"
                            aria-label={t('login.accountLabel')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[
                            {
                                required: true,
                                message: t('validation.passwordRequired'),
                            },
                        ]}
                    >
                        <Input.Password
                            className="login-autofill-field"
                            prefix={<LockOutlined />}
                            placeholder={t('login.passwordPlaceholder')}
                            size="large"
                            autoComplete="current-password"
                            aria-label={t('login.passwordLabel')}
                        />
                    </Form.Item>
                </AuthForm>
            </section>
        </main>
    )
}

export default Login
